import type { ISchedule } from '@/entities/booking/model/types';
import type { IScheduleInMedcenter } from '@/entities/medcenter/model/types';

/**
 * Вычисляет количество дней от текущей даты до указанной даты.
 *
 * @param {string} targetDate - Целевая дата в формате YYYY-MM-DD.
 * @returns {number} Количество дней (может быть отрицательным для прошедших дат).
 */
export const getDaysFromToday = (targetDate: string): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);

  const diffTime = target.getTime() - today.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Получает уникальное расписание, убирая дубликаты по дате.
 *
 * @param {ISchedule[]} schedule - Массив элементов расписания.
 * @returns {ISchedule[]} Уникальное расписание.
 */
export const getUniqueSchedule = (schedule: ISchedule[]): ISchedule[] => {
  return schedule.filter((item, index, self) => self.findIndex(i => i.date === item.date) === index);
};

/**
 * Форматирует расписание для отображения в медцентре.
 *
 * @param {ISchedule[]} schedule - Исходное расписание.
 * @returns {IScheduleInMedcenter[]} Отформатированное расписание.
 */
export const formatScheduleForMedcenter = (schedule: ISchedule[]): IScheduleInMedcenter[] => {
  const uniqueSchedule = getUniqueSchedule(schedule);

  return uniqueSchedule.map(day => ({
    day: day.day,
    isWork: day.isWork,
    startTime: day.timeslots?.[0] || null,
    endTime: day.timeslots?.[day.timeslots.length - 1] || null,
    is24HoursWork: day.is24HoursWork || false,
  }));
};

export const formatWorkingDaysForModal = (schedule: ISchedule[]): IScheduleInMedcenter[] => {
  if (schedule.length === 0) return [];

  const uniqueSchedule = getUniqueSchedule(schedule);

  const workingDays = uniqueSchedule.filter(
    day => day.isWork && day.timeslots && day.timeslots.length > 0 && getDaysFromToday(day.date) >= 0,
  );

  const sortedWorkingDays = workingDays.sort((a, b) => {
    const daysFromTodayA = getDaysFromToday(a.date);
    const daysFromTodayB = getDaysFromToday(b.date);
    return daysFromTodayA - daysFromTodayB;
  });

  const firstThreeWorkingDays = sortedWorkingDays.slice(0, 3);

  return firstThreeWorkingDays.map(day => ({
    day: day.day,
    isWork: day.isWork,
    startTime: day.timeslots?.[0] || null,
    endTime: day.timeslots?.[day.timeslots.length - 1] || null,
    is24HoursWork: day.is24HoursWork || false,
  }));
};

export const findNearestWorkDay = (schedule: ISchedule[]): ISchedule | null => {
  if (schedule.length === 0) return null;

  const uniqueSchedule = getUniqueSchedule(schedule);

  const workingDays = uniqueSchedule.filter(
    day => day.isWork && day.timeslots && day.timeslots.length > 0 && getDaysFromToday(day.date) >= 0,
  );

  if (workingDays.length === 0) return null;

  const sortedWorkingDays = workingDays.sort((a, b) => {
    const daysFromTodayA = getDaysFromToday(a.date);
    const daysFromTodayB = getDaysFromToday(b.date);
    return daysFromTodayA - daysFromTodayB;
  });

  return sortedWorkingDays[0];
};

export const getDayOffset = (scheduleDay: number, currentDay: number): number => {
  return scheduleDay - currentDay;
};
